/* eslint-disable @typescript-eslint/semi */
import {environment} from '../../environments/environment';

export class TorchUtils {
  public static printDevLog(...args) {
    if (!environment.production) {
      console.log(...args);
    }
  }

  public static getDatePart(initDate) {
    let utcMidnightDateObj = null;
    // if no date supplied, use Now.
    if (!initDate) {
      initDate = new Date();
    }

    // if initDate specifies a timezone offset, or is already UTC, just keep the date part, reflecting the date _in that timezone_
    if (typeof initDate === 'string' && initDate.match(/((\+|-)\d{2}:\d{2}|Z)$/gm)) {
      utcMidnightDateObj = new Date( initDate.substring(0, 10) + 'T00:00:00Z');
    } else {
      // if init date is not already a date object, feed it to the date constructor.
      if (!(initDate instanceof Date)) {
        initDate = new Date(initDate);
      }

      // Vital Step! Strip time part. Create UTC midnight dateObj according to local timezone.
      utcMidnightDateObj = new Date(Date.UTC(initDate.getFullYear(), initDate.getMonth(), initDate.getDate()));
    }

    return {
      toISOString: () => utcMidnightDateObj.toISOString(),
      toISOStringDaysOffset: (days) => new Date(utcMidnightDateObj.setDate(utcMidnightDateObj.getDate() + days)).toISOString(),
      toISODateString: () => utcMidnightDateObj.toISOString().split('T', 1)[0],
      toISODateStringDaysOffset: (days) => {
        return new Date(utcMidnightDateObj.setDate(utcMidnightDateObj.getDate() + days)).toISOString().split('T', 1)[0];
      },
      getUTCDate: () => utcMidnightDateObj.getUTCDate(),
      getUTCDay: () => utcMidnightDateObj.getUTCDay(),
      getUTCFullYear: () => utcMidnightDateObj.getUTCFullYear(),
      getUTCMonth: () => utcMidnightDateObj.getUTCMonth(),
      setUTCDate: (arg) => utcMidnightDateObj.setUTCDate(arg),
      setUTCFullYear: (arg) => utcMidnightDateObj.setUTCFullYear(arg),
      setUTCMonth: (arg) => utcMidnightDateObj.setUTCMonth(arg),
      addDays: (days) => {
        utcMidnightDateObj.setUTCDate(utcMidnightDateObj.getUTCDate + days);
      },
      toString: () => utcMidnightDateObj.toString(),
      toLocaleDateString: (locale?, options?) => {
        options = options || {};
        options.timeZone = 'UTC';
        locale = locale || 'en-EN';

        return utcMidnightDateObj.toLocaleDateString(locale, options);
      },
      toISOStringYearsOffset: (years) =>  {
        return new Date(utcMidnightDateObj.getUTCFullYear() + years, utcMidnightDateObj.getUTCMonth(), 1).toISOString();
      },
      toISODateStringYearsOffset: (years) =>  {
        return new Date(utcMidnightDateObj.getUTCFullYear() + years, utcMidnightDateObj.getUTCMonth(), 1).toISOString().split('T', 1)[0];
      },
      toISOStringMonthsOffset: (months) =>  {
        return new Date(utcMidnightDateObj.getUTCFullYear(), utcMidnightDateObj.getUTCMonth() + months, 1).toISOString();
      },
      toISODateStringMonthsOffset: (months) =>  {
        return new Date(utcMidnightDateObj.getUTCFullYear(), utcMidnightDateObj.getUTCMonth() + months, 1).toISOString().split('T', 1)[0];
      },
      toISODateStringYearMonthOffsetFirst: (years, months) => {
        return new Date(utcMidnightDateObj.getUTCFullYear() + years, utcMidnightDateObj.getUTCMonth() + months, 1).toISOString().split('T', 1)[0];
      }
    };
  }

  public static fromUTC(datetime) {
    // EXAMPLE:
    // const showTime = new Date(fromUTC(saveTime));
    // FROM DB...

    const myDate = (typeof datetime === 'number')
      ? new Date(datetime)
      : datetime;

    if (!myDate || (typeof myDate.getTime !== 'function')) {
      return 0;
    }

    const getUTC = myDate.getTime();
    const offset = myDate.getTimezoneOffset() * 60000; // It's in minutes so convert to ms
    return getUTC + offset; // UTC + OFFSET
  }

  public static toUTC(datetime) {
    // EXAMPLE:
    // const saveTime = new Date(toUTC(Date.parse("2005-07-08T00:00:00+0000")));
    // SEND TO DB....

    const myDate = (typeof datetime === 'number')
      ? new Date(datetime)
      : datetime;

    if (!myDate || (typeof myDate.getTime !== 'function')) {
      return 0;
    }

    const getUTC = myDate.getTime();
    const offset = myDate.getTimezoneOffset() * 60000; // It's in minutes so convert to ms
    return getUTC - offset; // UTC - OFFSET
  }

  public static parseTimestamp(timestampStr): Date {
    if (timestampStr) {
        return new Date(new Date(timestampStr).getTime() + (new Date(timestampStr).getTimezoneOffset() * 60 * 1000));
    } else {
        return null;
    }
  }

  public static getDateDifferences(date1: string, date2: string) {
    const sDate = new Date(date1);
    const eDate = new Date(date2);

    const timeDifference = eDate.getTime() - sDate.getTime();
    const daysDifference = timeDifference / (1000 * 3600 * 24);

    let timeframeType;
    let midDate;
    let firstDate;

    if (daysDifference >= 730) {
        timeframeType = 'YOY';
        midDate = new Date(eDate.setDate(eDate.getDate() - 366)).toISOString().slice(0, -14);
        firstDate = new Date(eDate.setDate(eDate.getDate() - 365)).toISOString().slice(0, -14);
    } else if (daysDifference < 730 && daysDifference >= 56) {
        timeframeType = 'MOM';
        midDate = new Date(eDate.setDate(eDate.getDate() - 31)).toISOString().slice(0, -14);
        firstDate = new Date(eDate.setDate(eDate.getDate() - 30)).toISOString().slice(0, -14);
    } else {
        timeframeType = 'ERROR';
        midDate = '9999-12-31';
        firstDate = '9999-12-31';
    }

    return { timeframeType, midDate, firstDate };
  }

  public static getValidDateDifferences(date1: string, date2: string) {
    const sDate = new Date(date1);
    const eDate = new Date(date2);

    const timeDifference = eDate.getTime() - sDate.getTime();
    const daysDifference = timeDifference / (1000 * 3600 * 24);

    let timeframeType;
    let midDate;
    let firstDate;

    if (daysDifference >= 365) {
      timeframeType = 'YOY';
      midDate = new Date(eDate.setDate(eDate.getDate() - 366)).toISOString().slice(0, -14);
      firstDate = new Date(eDate.setDate(eDate.getDate() - 730)).toISOString().slice(0, -14);
    } else if (daysDifference < 365 && daysDifference >= 28) {
      timeframeType = 'MOM';
      midDate = new Date(eDate.setDate(eDate.getDate() - 31)).toISOString().slice(0, -14);
      firstDate = new Date(eDate.setDate(eDate.getDate() - 62)).toISOString().slice(0, -14);
    } else {
      timeframeType = 'ERROR';
      midDate = '9999-12-31';
      firstDate = '9999-12-31';
    }

    return { timeframeType, midDate, firstDate };
  }

  public static titlecase(str: string): string {
    if (!!str) {
      return str.split(' ')
        .map(w => w[0].toUpperCase() + w.substr(1).toLowerCase())
        .join(' ');
    }

    return str;
  }

  public static snakeToCamel = str =>
    str.toLowerCase().replace(/([-_][a-z])/g, group =>
      group
        .toUpperCase()
        .replace('-', '')
        .replace('_', '')
    )

  public static downloadExcelSpreadsheet(fileName: string, blob: Blob) {
    const downloadLink = document.createElement('a');

    downloadLink.download = fileName;
    downloadLink.href = window.URL.createObjectURL(blob);
    downloadLink.dataset.downloadurl = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      downloadLink.download,
      downloadLink.href
    ].join(':');

    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
  }
}
